import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Public feed of published pins from the Cerveza Data Standard catalog
 * (Venues + Brewery Sites). Anonymous SELECT is granted via RLS
 * (publish_status='PUBLISHED'). Returns only what is safe for the map.
 */
export type ReviewStatus = "DISCOVERY_ONLY" | "PENDING_REVIEW" | "VERIFIED" | "REJECTED";

export type CatalogMapPin = {
  id: string;
  layer: "venue" | "brewery_site";
  name: string;
  latitude: number;
  longitude: number;
  city: string | null;
  country_iso2: string | null;
  country_name: string | null;
  continent_code: string | null;
  kind: string | null;
  review_status: ReviewStatus | null;
};

/**
 * Admin/editor-only: includes unpublished brewery sites (draft, pending, rejected).
 * Guarded by requireSupabaseAuth + explicit role check to prevent anonymous access
 * to internal catalog data.
 */
export const listCatalogMapPins = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CatalogMapPin[]> => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    const { data: isEditor } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "catalog_editor",
    });
    if (!isAdmin && !isEditor) {
      throw new Response("Forbidden: admin or catalog_editor role required", { status: 403 });
    }
    return fetchCatalogPins({ publicOnly: false });
  });


/**
 * Public-safe variant: only PUBLISHED venues and PUBLISHED brewery sites.
 * Used by the /mapa-verejna page which anyone can view without signing in.
 */
export const listPublicCatalogMapPins = createServerFn({ method: "GET" }).handler(
  async (): Promise<CatalogMapPin[]> => {
    return fetchCatalogPins({ publicOnly: true });
  },
);

async function fetchCatalogPins({ publicOnly }: { publicOnly: boolean }): Promise<CatalogMapPin[]> {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");


    async function fetchAll<T>(
      table: "cid_venues" | "cid_brewery_sites",
      selectCols: string,
      onlyPublished: boolean,
    ): Promise<T[]> {
      const pageSize = 1000;
      const all: T[] = [];
      for (let from = 0; ; from += pageSize) {
        let q = supabaseAdmin
          .from(table)
          .select(selectCols)
          .not("latitude", "is", null)
          .not("longitude", "is", null)
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        if (onlyPublished) q = q.eq("publish_status", "PUBLISHED");
        const { data, error } = await q;
        if (error) throw error;
        const rows = (data ?? []) as unknown as T[];
        all.push(...rows);
        if (rows.length < pageSize) break;
      }
      return all;
    }

    const [venues, sites] = await Promise.all([
      fetchAll<Record<string, unknown>>(
        "cid_venues",
        "id, official_name, latitude, longitude, venue_type, cid_cities(official_name), cid_countries(iso2, short_name, local_name, continent_code)",
        true,
      ),
      fetchAll<Record<string, unknown>>(
        "cid_brewery_sites",
        "id, official_name, latitude, longitude, site_type, review_status, cid_cities(official_name), cid_countries(iso2, short_name, local_name, continent_code)",
        publicOnly,
      ),
    ]);

    type CountryJoin = {
      iso2?: string;
      short_name?: string | null;
      local_name?: string | null;
      continent_code?: string | null;
    } | null;

    function countryFields(raw: unknown): {
      iso2: string | null;
      name: string | null;
      continent: string | null;
    } {
      const c = raw as CountryJoin;
      if (!c) return { iso2: null, name: null, continent: null };
      const iso2 = c.iso2 ?? null;
      const name = c.short_name ?? c.local_name ?? iso2;
      return { iso2, name, continent: c.continent_code ?? null };
    }

    const pins: CatalogMapPin[] = [];
    for (const v of venues) {
      const cf = countryFields(v.cid_countries);
      pins.push({
        id: v.id as string,
        layer: "venue",
        name: (v.official_name as string) ?? "",
        latitude: Number(v.latitude),
        longitude: Number(v.longitude),
        city: (v.cid_cities as { official_name?: string } | null)?.official_name ?? null,
        country_iso2: cf.iso2,
        country_name: cf.name,
        continent_code: cf.continent,
        kind: (v.venue_type as string) ?? null,
        review_status: null,
      });
    }
    for (const s of sites) {
      const cf = countryFields(s.cid_countries);
      pins.push({
        id: s.id as string,
        layer: "brewery_site",
        name: (s.official_name as string) ?? "",
        latitude: Number(s.latitude),
        longitude: Number(s.longitude),
        city: (s.cid_cities as { official_name?: string } | null)?.official_name ?? null,
        country_iso2: cf.iso2,
        country_name: cf.name,
        continent_code: cf.continent,
        kind: (s.site_type as string) ?? null,
        review_status: (s.review_status as ReviewStatus) ?? "DISCOVERY_ONLY",
      });
    }
    return pins;
}

